import type { HotspotAppearance } from '@/lib/hotspotButton'

// テロップ情報
export interface Telop {
  text: string
  startTime: number // 開始時間（秒）
  endTime: number // 終了時間（秒）
}

// アンケート設問型。SINGLE=単一選択 / MULTI=複数選択 / FREE=自由回答。
export type SurveyQuestionType = 'SINGLE' | 'MULTI' | 'FREE'

// リード（PII）項目型。設問 leadFieldKind が非nullなら PII 入力欄で、値はリード表へ隔離保存される
// （EMAIL は形式検証を継承・ADR-0012・#262）。当面 EMAIL のみ提供（他型は #264 で解禁）。
// プレイヤー描画用に単純化した型で、orval 生成の LeadFieldKind と同値（SurveyQuestionType と同様に
// 公開DTOから意図的に分離）。値を増やす際は両方（＋バックエンド models.ts / Prisma enum）を揃える。
export type LeadFieldKind = 'EMAIL' | 'NAME' | 'COMPANY' | 'PHONE' | 'DEPARTMENT' | 'TEXT'

// アンケート選択肢（プレイヤー描画用に単純化した型）
export interface SurveyOption {
  id: string
  label: string
}

// アンケート設問（プレイヤー描画用に単純化した型）
// 公開DTO SurveyQuestionWithOptions から必要フィールドだけを抜き出したもの。
export interface SurveyQuestion {
  id: string
  text: string
  questionType: SurveyQuestionType
  required: boolean
  // リード（PII）項目型。非nullなら PII 入力欄を出し値をリード表へ隔離保存する（questionType 無視・ADR-0012）。
  leadFieldKind: LeadFieldKind | null
  // 提示時刻（秒）。null = 末尾（VIDEO_END）でまとめて提示（シーケンス）。
  cueTime: number | null
  order: number
  options: SurveyOption[]
}

// アンケート回答（1設問ぶん）。SINGLE=optionIds 1件 / MULTI=複数 / FREE=freeText。
// leadValue はリード（PII）項目の設問で入力された値（回答とは別テーブルへ隔離保存される）。
export interface SurveyAnswer {
  optionIds: string[]
  freeText: string | null
  leadValue?: string | null
}

// ノード種別。SURVEY は POPUP 専用の全画面ゲート（動画を持たない・#263）。
export type NodeKind = 'VIDEO' | 'LINK' | 'SURVEY'

// SURVEYノードの config（不透明JSONだが SURVEY 用に必要な形を型付け・#263/#265）。
// 遷移先マップは再利用テンプレ（Survey）ではなく配置（SURVEYノード）側の config に保持する（ADR-0011）。
export interface SurveyNodeConfig {
  // キャンバス上の座標（位置保存と共有）。
  x?: number
  y?: number
  // 送信後のデフォルト遷移先（nodeKey）。ルーティング未該当/未指定（線形）はここへ進む（#263）。
  defaultTargetNodeKey?: string | null
  // ルーティング設問の設問ID（単一選択）。指定なし＝線形（デフォルトのみ）（#265・ADR-0011）。
  routingQuestionId?: string | null
  // 遷移先マップ（ルーティング設問の optionId → 遷移先 nodeKey）。未該当は defaultTargetNodeKey（#265）。
  routing?: Record<string, string> | null
}

// シナリオノード（各動画の情報）
// SURVEYノードの遷移先（defaultTargetNodeKey / routingQuestionId / routing）は config と同じ形なので
// SurveyNodeConfig から取り込む。buildSurveyGateNode が node.config から値を載せる（#263/#265）。
export interface ScenarioNode extends Pick<SurveyNodeConfig, 'defaultTargetNodeKey' | 'routingQuestionId' | 'routing'> {
  id: string
  nodeKey: string // ノードのキー（アナリティクス用）
  // ノード種別。未指定は VIDEO 相当（後方互換）。SURVEY のとき全画面ゲートを描画する。
  type?: NodeKind
  videoUrl: string
  hlsUrl?: string | null // HLSマスタープレイリストURL（READY時のみ）。未指定/null なら videoUrl(MP4) で再生
  title?: string
  buttons: InteractiveButton[]
  telops?: Telop[] // 複数のテロップ情報
  surveyQuestions?: SurveyQuestion[] // アンケート設問（提示タイミング付き）
  analytics?: AnalyticsConfig
  /**
   * ワイド型の分岐で、選択肢の上に出す問いかけ（ノードの config が持つ・ADR-0016）。
   * 「今日の営業。どの課題から向き合いますか？」のような一文を想定。
   */
  branchQuestionText?: string | null
  /** WIDE型の選択肢表示時刻。0=最初、正数=指定時刻（秒）、null=末尾。 */
  branchCueTime?: number | null
  /**
   * 動画CTA（遷移先）。動画が唯一の持ち主（ADR-0015）。
   * ワイド型の終了画面で主CTAとして出す。
   */
  ctaUrl?: string | null
  ctaLabel?: string | null
  /**
   * 動画内のクリック領域（ワイド型の動画ノードのみ・ADR-0022）。
   * 見た目（appearance）が無ければ、ボタンの絵は動画に焼き込み、プレイヤーは透明な領域だけを重ねる。
   * 見た目があれば、プレイヤーがボタンを描く（ADR-0024）。
   * 並び順が前のものほど優先（重なった場所では前が押される）。
   */
  hotspots?: Hotspot[]
}

/** 映像フレームを 0〜1 とした矩形（コンテナ基準ではない・ADR-0022） */
export interface HotspotRect {
  x: number
  y: number
  w: number
  h: number
}

/** クリック領域を押したときの動作 */
export type HotspotAction =
  | { type: 'seek'; time: number }
  | { type: 'video_transition'; targetNodeId: string; startTime: number }
  | { type: 'external_link'; url: string; openInNewTab: boolean }

/** 動画内のクリック領域（ホットスポット・ADR-0022）・見えるボタン（ADR-0024） */
export interface Hotspot {
  id: string
  /** 計測・読み上げ用の名前 */
  label: string
  /** 表示開始（秒） */
  startTime: number
  /** 表示終了（秒） */
  endTime: number
  rect: HotspotRect
  action: HotspotAction
  /** null = 透明なクリック領域。値があればプレイヤーがボタンを描き、label をボタンの文字にする（ADR-0024） */
  appearance: HotspotAppearance | null
}

// インタラクティブボタン
export interface InteractiveButton {
  id: string
  label: string
  type: ButtonActionType
  action: ButtonAction
  position?: ButtonPosition // カスタム位置指定
  style?: ButtonStyle // カスタムスタイル
}

// ボタンアクションタイプ
export enum ButtonActionType {
  VIDEO_TRANSITION = 'video_transition',
  EXTERNAL_LINK = 'external_link',
}

// ボタンアクション
export type ButtonAction =
  | { type: 'video_transition'; targetNodeId: string }
  | { type: 'external_link'; url: string; openInNewTab: boolean }

// ボタン位置
export interface ButtonPosition {
  x?: number // 0-100% の相対位置
  y?: number // 0-100% の相対位置
  alignment?: 'left' | 'center' | 'right'
}

// ボタンスタイル
export interface ButtonStyle {
  backgroundColor?: string
  textColor?: string
  borderRadius?: string
  fontSize?: string
}

// プレイヤー設定
export interface PlayerConfig {
  initialNodeId: string
  nodes: Map<string, ScenarioNode>
  autoplay?: boolean
  muted?: boolean
  showProgress?: boolean
  analyticsEnabled?: boolean
  /**
   * テナントのデフォルトCTAボタン色（不透明HEX）。
   * 個別ボタンに色指定が無い場合のフォールバックに使う。未設定なら null/undefined。
   */
  defaultButtonColor?: string | null
  /**
   * ワイド型の目次をオンにした動画（ADR-0025）。その動画を見ている間、動画のインタラクティブを目次に並べる。
   * 未設定なら undefined で、目次は出さない。
   */
  agenda?: AgendaItem[]
}

/** 目次をオンにした動画。目次の行はその動画のインタラクティブから作る */
export interface AgendaItem {
  nodeId: string
}

// プレイヤー状態
export interface PlayerState {
  currentNodeId: string
  history: string[] // ノードIDのスタック
  isPlaying: boolean
  currentTime: number
  duration: number
  volume: number
  isMuted: boolean
  playbackRate: number // 再生速度（1.0 = 通常速度）
  /**
   * 現在のノードの動画が最後まで再生されたか。
   * ワイド型は「最終フレームで静止したまま暗転＋選択肢」を出すのに使う（ADR-0016）。
   * ノードを移動したら false に戻す。
   */
  hasEnded: boolean
}

// アナリティクス設定
export interface AnalyticsConfig {
  trackViews?: boolean
  trackClicks?: boolean
  trackCompletion?: boolean
  customEvents?: Record<string, unknown>
}

// エラークラス
export class VideoPlayerError extends Error {
  constructor(
    message: string,
    public code: ErrorCode,
    public details?: unknown,
  ) {
    super(message)
    this.name = 'VideoPlayerError'
  }
}

// エラーコード
export enum ErrorCode {
  VIDEO_LOAD_FAILED = 'VIDEO_LOAD_FAILED',
  INVALID_NODE_ID = 'INVALID_NODE_ID',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SHADOW_DOM_NOT_SUPPORTED = 'SHADOW_DOM_NOT_SUPPORTED',
}

// アナリティクスイベント
export interface AnalyticsEvent {
  type: 'video_start' | 'video_end' | 'button_click' | 'video_switch' | 'external_link'
  nodeId: string
  timestamp: number
  data?: Record<string, unknown>
}

// レイアウト設定
export interface LayoutConfig {
  videoMaxWidth: string
  videoAspectRatio: string
  buttonSize: 'small' | 'medium' | 'large'
  buttonSpacing: number
}

// ボタンレイアウト
export interface ButtonLayout extends InteractiveButton {
  calculatedStyle: {
    position: string
    left: string
    top: string
    transform: string
    zIndex: number
  }
}
